'use client'

import { FC, useMemo, useState, useEffect } from 'react'
import { testIds } from '@/root/shared-consts'
import { DateTime } from 'luxon'
import { FormDropdown } from '@/root/modules/shared/SelectTicketPage/components/PassengerForm'
import { useGetTranslations } from '@/root/shared-hooks'

interface Props {
  birthdate: string
  onChange: (value: string) => void
  disabled?: boolean
}

export const BirthDateSelect: FC<Props> = ({ birthdate, onChange, disabled = false }) => {
  const { getTranslation } = useGetTranslations()

  const initialDate = birthdate && DateTime.fromISO(birthdate).isValid ? DateTime.fromISO(birthdate) : null

  const [day, setDay] = useState<number | ''>(initialDate?.day ?? '')
  const [month, setMonth] = useState<number | ''>(initialDate?.month ?? '')
  const [year, setYear] = useState<number | ''>(initialDate?.year ?? '')

  // FIX: sync with react-hook-form reset
  useEffect(() => {
    if (!birthdate) {
      setDay('')
      setMonth('')
      setYear('')
      return
    }

    const newDate = DateTime.fromISO(birthdate)

    if (newDate.isValid) {
      setDay(newDate.day)
      setMonth(newDate.month)
      setYear(newDate.year)
    }
  }, [birthdate])

  const currentYear = DateTime.now().year

  const years = useMemo(
    () =>
      Array.from({ length: 120 }, (_, i) => {
        const y = currentYear - i
        return { value: y, label: String(y) }
      }),
    [currentYear]
  )

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const m = i + 1
        return {
          value: m,
          label: String(m).padStart(2, '0'),
        }
      }),
    []
  )

  const daysInMonth = year && month ? DateTime.local(Number(year), Number(month)).daysInMonth || 31 : 31

  const days = useMemo(
    () =>
      Array.from({ length: daysInMonth }, (_, i) => {
        const d = i + 1
        return {
          value: d,
          label: String(d).padStart(2, '0'),
        }
      }),
    [daysInMonth]
  )

  useEffect(() => {
    if (disabled) return

    if (day && month && year) {
      const newDate = DateTime.local(year, month, day)

      if (newDate.isValid) {
        onChange(newDate.toISODate()!)
      }
    } else {
      onChange(`${year}-${month}-${day}`)
    }
  }, [day, month, year, onChange, disabled])

  return (
    <div className="grid grid-cols-3 gap-2">
      <FormDropdown
        value={day}
        options={days}
        onChange={(d) => !disabled && setDay(Number(d))}
        className="w-full"
        disabled={disabled}
        placeholder={getTranslation('selectDayPlaceholder')}
        testId={testIds.tickets.passengerBirthdateDay}
      />
      <FormDropdown
        value={month}
        options={months}
        onChange={(m) => !disabled && setMonth(Number(m))}
        className="w-full"
        disabled={disabled}
        placeholder={getTranslation('selectMonthPlaceholder')}
        testId={testIds.tickets.passengerBirthdateMonth}
      />
      <FormDropdown
        value={year}
        options={years}
        onChange={(y) => !disabled && setYear(Number(y))}
        className="w-full"
        disabled={disabled}
        placeholder={getTranslation('selectYearPlaceholder')}
        testId={testIds.tickets.passengerBirthdateYear}
      />
    </div>
  )
}
